import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Pencil, Trash2, FileText, Upload, PlusCircle, Package,
  Mail, Send, Eye, CheckCircle2, Loader2, RotateCcw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_TYPE_LABELS = {
  seat_selection:   "Seat Selection",
  extra_baggage:    "Extra Baggage",
  lounge_access:    "Lounge Access",
  fast_track:       "Fast Track",
  meet_and_greet:   "Meet & Greet",
  airport_transfer: "Airport Transfer",
  visa_assistance:  "Visa Assistance",
  hotel:            "Hotel",
  airport_dining:   "Airport Dining",
  airport_spa:      "Airport Spa",
  other:            "Other",
};

const SERVICE_STATUS_VARIANTS = {
  pending:   "secondary",
  confirmed: "outline",
  delivered: "default",
  cancelled: "destructive",
};

const VAT_TYPES = ["0% VAT", "Dutch VAT 21%", "Dutch VAT 9%", "Exempt"];

const BLANK_FORM = {
  service_type: "",
  description: "",
  supplier_name: "",
  passenger_name: "",
  segment_info: "",
  service_date: "",
  confirmation_reference: "",
  amount: "",
  supplier_cost: "",
  vat_type: "0% VAT",
  payment_mode: "Bank Transfer",
  payment_status: "Paid",
  date_received: "",
  amount_received: "",
  service_status: "pending",
  notes: "",
};

/**
 * Parse a numeric string that may use European decimal/thousand separators.
 *
 * "71,41"     → 71.41   (comma = decimal separator)
 * "71.41"     → 71.41   (standard)
 * "1.000"     → 1000    (European: dot = thousand separator, no decimal part)
 * "1.000,00"  → 1000.00 (full European: dots = thousands, comma = decimal)
 * "1.234,56"  → 1234.56
 * ""           → null
 * "abc"        → null
 *
 * Returns null for empty/invalid input — never returns NaN.
 */
function parseEuroNum(raw) {
  if (raw === "" || raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  if (s === "") return null;

  // Pattern 1 — full European: thousands separated by dots (or spaces), decimal comma
  //   e.g. "1.000,00"  "1.234,56"  "71,41"
  const euroComma = /^-?\d{1,3}(?:[.\s]\d{3})*,\d{1,2}$/.test(s);

  // Pattern 2 — European thousands-only (no decimal part, dots group 3 digits)
  //   e.g. "1.000"  "1.000.000"
  const euroThousandsOnly = /^-?\d{1,3}(\.\d{3})+$/.test(s);

  let normalised;
  if (euroComma) {
    // Remove thousand separators (dots/spaces) then replace decimal comma with dot
    normalised = s.replace(/[.\s]/g, "").replace(",", ".");
  } else if (euroThousandsOnly) {
    // All dots are thousand separators — strip them
    normalised = s.replace(/\./g, "");
  } else {
    // Standard or mixed: replace any remaining commas with dots
    normalised = s.replace(/,/g, ".");
  }
  const n = parseFloat(normalised);
  return isNaN(n) ? null : n;
}

export default function ExtraServicesCard({ booking, onUpdate }) {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [services, setServices]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showForm, setShowForm]         = useState(false);
  const [editId, setEditId]             = useState(null);
  const [form, setForm]                 = useState(BLANK_FORM);
  const [saving, setSaving]             = useState(false);
  const [uploadingId, setUploadingId]   = useState(null);
  const [generatingInvId, setGeneratingInvId] = useState(null);

  // Email workflow state
  const [previewEmailId, setPreviewEmailId]   = useState(null); // service id being previewed
  const [previewData, setPreviewData]         = useState(null); // {subject,html,to,from}
  const [previewLoading, setPreviewLoading]   = useState(false);
  const [sendingEmailId, setSendingEmailId]   = useState(null); // id for test send spinner
  const [sendCustomerModal, setSendCustomerModal] = useState(null); // svc object for confirm dialog
  const [sendingCustomer, setSendingCustomer] = useState(false);
  const [recipientOverride, setRecipientOverride] = useState("");
  const [resolvedRecipient, setResolvedRecipient] = useState(null); // null=loading, ""=not found, str=email
  const resolveRequestRef = useRef(null); // tracks which svc.id the in-flight resolve belongs to

  // Refund workflow state
  const [refundModal, setRefundModal]   = useState(null); // svc object being refunded
  const [refundForm, setRefundForm]     = useState({ customer_refund_amount: "", supplier_refund_amount: "", customer_refund_date: "", supplier_refund_date: "", cancellation_reason: "airline_rescheduled", refund_notes: "" });
  const [refunding, setRefunding]       = useState(false);

  // Refund email workflow state
  const [refundEmailPreviewId, setRefundEmailPreviewId]         = useState(null);
  const [refundEmailPreviewData, setRefundEmailPreviewData]     = useState(null);
  const [refundEmailPreviewLoading, setRefundEmailPreviewLoading] = useState(false);
  const [sendingRefundEmailId, setSendingRefundEmailId]         = useState(null);
  const [refundEmailCustomerModal, setRefundEmailCustomerModal] = useState(null);
  const [refundEmailRecipientOverride, setRefundEmailRecipientOverride] = useState("");
  const [refundEmailResolvedRecipient, setRefundEmailResolvedRecipient] = useState(null);
  const [sendingRefundEmailCustomer, setSendingRefundEmailCustomer]     = useState(false);
  const refundEmailResolveRef = useRef(null);

  const fetchServices = useCallback(async () => {
    if (!booking?.id) return;
    try {
      const res = await axios.get(
        `${API}/bookings/${booking.id}/extra-services`,
        { headers: getAuthHeaders() }
      );
      setServices(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load extra services");
    } finally {
      setLoading(false);
    }
  }, [booking?.id, getAuthHeaders]);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  function openAdd() {
    setEditId(null);
    setForm(BLANK_FORM);
    setShowForm(true);
  }

  function openEdit(svc) {
    setEditId(svc.id);
    setForm({
      service_type:           svc.service_type || "",
      description:            svc.description || "",
      supplier_name:          svc.supplier_name || "",
      passenger_name:         svc.passenger_name || "",
      segment_info:           svc.segment_info || "",
      service_date:           svc.service_date || "",
      confirmation_reference: svc.confirmation_reference || "",
      amount:                 svc.amount ?? "",
      supplier_cost:          svc.supplier_cost ?? "",
      vat_type:               svc.vat_type || "0% VAT",
      payment_mode:           svc.payment_mode || "Bank Transfer",
      payment_status:         svc.payment_status || "Paid",
      date_received:          svc.date_received || "",
      amount_received:        svc.amount_received ?? "",
      service_status:         svc.service_status || "pending",
      notes:                  svc.notes || "",
    });
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.description.trim()) {
      toast.error("Description is required");
      return;
    }

    // Parse selling price — accept both period and European comma decimal separators
    const amountParsed = parseEuroNum(form.amount);
    if (amountParsed === null) {
      toast.error("Selling price is required");
      return;
    }
    if (amountParsed <= 0) {
      toast.error("Selling price must be greater than zero");
      return;
    }

    const supplierCostParsed   = parseEuroNum(form.supplier_cost);   // null → 0 below
    const amountReceivedParsed = parseEuroNum(form.amount_received);  // null stays null

    setSaving(true);
    try {
      const basePayload = {
        ...form,
        amount:          amountParsed,
        supplier_cost:   supplierCostParsed !== null ? supplierCostParsed : 0,
        amount_received: amountReceivedParsed,
        customer_name:   `${booking.passengers?.[0]?.first_name || ""} ${booking.passengers?.[0]?.last_name || ""}`.trim() || "Unknown",
        date:            form.service_date || new Date().toISOString().slice(0, 10),
      };
      // booking_id and linked_booking are set only on creation, never overwritten on edit
      const payload = editId
        ? basePayload
        : {
            ...basePayload,
            booking_id:     booking.id,
            linked_booking: booking.reference || booking.invoice_number || null,
          };
      if (editId) {
        await axios.patch(
          `${API}/accounting/service-entries/${editId}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success("Service updated");
      } else {
        await axios.post(
          `${API}/accounting/service-entries`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success("Service added");
      }
      setShowForm(false);
      fetchServices();
      if (onUpdate) onUpdate();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Pydantic v2 validation errors are an array; flatten to a readable string
      const msg = Array.isArray(detail)
        ? detail.map((e) => `${e.loc?.slice(-1)?.[0] ?? "field"}: ${e.msg}`).join("; ")
        : typeof detail === "string"
          ? detail
          : err.message || "Failed to save service";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(svc) {
    const hasHistory = svc.amount_received !== null && svc.amount_received !== undefined
      ? true : !!svc.date_received;
    const msg = hasHistory
      ? `This service has payment history and will be cancelled (not deleted) to preserve the audit trail. Continue?`
      : `Delete service "${svc.description}"?\n\nIf an invoice has already been generated, the service will be cancelled instead of deleted.`;
    if (!window.confirm(msg)) return;
    try {
      const res = await axios.delete(
        `${API}/accounting/service-entries/${svc.id}`,
        { headers: getAuthHeaders() }
      );
      toast.success(res.data?.soft_deleted ? "Service cancelled (audit trail preserved)" : "Service deleted");
      fetchServices();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete service");
    }
  }

  function openRefundModal(svc) {
    setRefundForm({
      customer_refund_amount: String(svc.amount ?? ""),
      supplier_refund_amount: String(svc.supplier_cost ?? "0"),
      customer_refund_date: new Date().toISOString().slice(0, 10),
      supplier_refund_date: "",
      cancellation_reason: "airline_rescheduled",
      refund_notes: "",
    });
    setRefundModal(svc);
  }

  async function handleConfirmRefund() {
    const customerAmt = parseEuroNum(refundForm.customer_refund_amount);
    if (!customerAmt || customerAmt <= 0) {
      toast.error("Customer refund amount must be greater than zero");
      return;
    }
    setRefunding(true);
    try {
      await axios.post(
        `${API}/accounting/service-entries/${refundModal.id}/refund`,
        {
          customer_refund_amount: customerAmt,
          supplier_refund_amount: parseEuroNum(refundForm.supplier_refund_amount) ?? 0,
          customer_refund_date: refundForm.customer_refund_date || null,
          supplier_refund_date: refundForm.supplier_refund_date || null,
          cancellation_reason: refundForm.cancellation_reason || null,
          refund_notes: refundForm.refund_notes || null,
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Refund recorded and invoice cancelled");
      setRefundModal(null);
      fetchServices();
      if (onUpdate) onUpdate();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to record refund");
    } finally {
      setRefunding(false);
    }
  }

  async function handleGenerateInvoice(svc) {
    setGeneratingInvId(svc.id);
    try {
      const res = await axios.post(
        `${API}/invoices/from-source`,
        { source_type: "service_entry", source_id: svc.id },
        { headers: getAuthHeaders() }
      );
      toast.success(`Invoice ${res.data.invoice_number} created`);
      fetchServices();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.invoice?.id) {
        toast.info(`Invoice ${detail.invoice.invoice_number} already exists`);
        // Patch the in-memory service so the View link appears without a re-fetch
        setServices(prev => prev.map(s =>
          s.id === svc.id
            ? { ...s, _linked_invoice_id: detail.invoice.id, _linked_invoice_number: detail.invoice.invoice_number }
            : s
        ));
      } else if (err?.response?.status === 409) {
        // 409 but no usable invoice ID in the response — fetch to get current state
        toast.error("Invoice already exists. Refreshing service list…");
        fetchServices();
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to generate invoice");
      }
    } finally {
      setGeneratingInvId(null);
    }
  }

  async function handleUploadDoc(svc, file) {
    setUploadingId(svc.id);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result.split(",")[1]);
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });
      const uploadRes = await axios.post(
        `${API}/uploads/receipt`,
        { file_data: base64, file_name: file.name, mime_type: file.type },
        { headers: getAuthHeaders() }
      );
      const url = uploadRes.data?.url;
      if (!url) throw new Error("Upload succeeded but no URL was returned");
      await axios.patch(
        `${API}/accounting/service-entries/${svc.id}`,
        { supplier_document_url: url },
        { headers: getAuthHeaders() }
      );
      toast.success("Document uploaded");
      fetchServices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Upload failed");
    } finally {
      setUploadingId(null);
    }
  }

  // ── Email workflow ─────────────────────────────────────────────────────────

  async function handlePreviewEmail(svc) {
    setPreviewEmailId(svc.id);
    setPreviewData(null);
    setPreviewLoading(true);
    try {
      const res = await axios.get(
        `${API}/accounting/service-entries/${svc.id}/confirmation-email-preview`,
        { headers: getAuthHeaders() }
      );
      setPreviewData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load email preview");
      setPreviewEmailId(null);
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleSendTestEmail(svc) {
    setSendingEmailId(svc.id);
    try {
      const res = await axios.post(
        `${API}/accounting/service-entries/${svc.id}/send-confirmation-email`,
        { is_test: true },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message || "Test email sent");
      fetchServices();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to send test email");
    } finally {
      setSendingEmailId(null);
    }
  }

  function openSendCustomerModal(svc) {
    setRecipientOverride("");
    setResolvedRecipient(null);
    setSendCustomerModal(svc);
    // Track this request so a stale response from a previously opened service cannot
    // overwrite resolvedRecipient for a different service (race: open A → close → open B
    // before A responds — A's callback must not clobber B's displayed address).
    resolveRequestRef.current = svc.id;
    axios.get(
      `${API}/accounting/service-entries/${svc.id}/confirmation-email-preview`,
      { headers: getAuthHeaders() }
    ).then(r => {
        if (resolveRequestRef.current === svc.id) setResolvedRecipient(r.data.to || "");
      })
     .catch(() => {
        if (resolveRequestRef.current === svc.id) setResolvedRecipient("");
      });
  }

  async function handleConfirmSendCustomer() {
    if (!sendCustomerModal) return;
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (recipientOverride.trim() && !EMAIL_RE.test(recipientOverride.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSendingCustomer(true);
    try {
      const body = { is_test: false };
      if (recipientOverride.trim()) body.recipient_email = recipientOverride.trim();
      const res = await axios.post(
        `${API}/accounting/service-entries/${sendCustomerModal.id}/send-confirmation-email`,
        body,
        { headers: getAuthHeaders() }
      );
      if (res.data.warning) {
        toast.warning(res.data.warning);
      } else {
        toast.success(res.data.message || "Confirmation email sent");
      }
      setSendCustomerModal(null);
      fetchServices();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to send confirmation email");
    } finally {
      setSendingCustomer(false);
    }
  }

  // ── Refund email workflow ──────────────────────────────────────────────────

  async function handlePreviewRefundEmail(svc) {
    setRefundEmailPreviewId(svc.id);
    setRefundEmailPreviewData(null);
    setRefundEmailPreviewLoading(true);
    try {
      const res = await axios.get(
        `${API}/accounting/service-entries/${svc.id}/refund-email-preview`,
        { headers: getAuthHeaders() }
      );
      setRefundEmailPreviewData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load refund email preview");
      setRefundEmailPreviewId(null);
    } finally {
      setRefundEmailPreviewLoading(false);
    }
  }

  async function handleSendTestRefundEmail(svc) {
    setSendingRefundEmailId(svc.id);
    try {
      const res = await axios.post(
        `${API}/accounting/service-entries/${svc.id}/send-refund-email`,
        { is_test: true },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message || "Test refund email sent");
      fetchServices();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to send test refund email");
    } finally {
      setSendingRefundEmailId(null);
    }
  }

  function openSendRefundCustomerModal(svc) {
    setRefundEmailRecipientOverride("");
    setRefundEmailResolvedRecipient(null);
    setRefundEmailCustomerModal(svc);
    refundEmailResolveRef.current = svc.id;
    axios.get(
      `${API}/accounting/service-entries/${svc.id}/refund-email-preview`,
      { headers: getAuthHeaders() }
    ).then(r => {
        if (refundEmailResolveRef.current === svc.id) setRefundEmailResolvedRecipient(r.data.to || "");
      })
     .catch(() => {
        if (refundEmailResolveRef.current === svc.id) setRefundEmailResolvedRecipient("");
      });
  }

  async function handleConfirmSendRefundCustomer() {
    if (!refundEmailCustomerModal) return;
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (refundEmailRecipientOverride.trim() && !EMAIL_RE.test(refundEmailRecipientOverride.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSendingRefundEmailCustomer(true);
    try {
      const body = { is_test: false };
      if (refundEmailRecipientOverride.trim()) body.recipient_email = refundEmailRecipientOverride.trim();
      const res = await axios.post(
        `${API}/accounting/service-entries/${refundEmailCustomerModal.id}/send-refund-email`,
        body,
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message || "Refund notification sent");
      setRefundEmailCustomerModal(null);
      fetchServices();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to send refund email");
    } finally {
      setSendingRefundEmailCustomer(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const visibleServices  = services.filter(s => s.service_status !== "cancelled");
  const cancelledServices = services.filter(s => s.service_status === "cancelled");

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Extra Services
              {visibleServices.length > 0 && (
                <Badge variant="secondary" className="ml-1">{visibleServices.length}</Badge>
              )}
            </span>
            <Button size="sm" variant="outline" onClick={openAdd}>
              <PlusCircle className="w-4 h-4 mr-1.5" />
              Add Service
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading && <p className="text-sm text-slate-400">Loading…</p>}

          {!loading && visibleServices.length === 0 && (
            <p className="text-sm text-slate-400 italic">No extra services recorded.</p>
          )}

          {visibleServices.map((svc) => (
            <ServiceRow
              key={svc.id}
              svc={svc}
              onEdit={() => openEdit(svc)}
              onDelete={() => handleDelete(svc)}
              onGenerateInvoice={() => handleGenerateInvoice(svc)}
              generatingInv={generatingInvId === svc.id}
              linkedInvoiceId={svc._linked_invoice_id || null}
              linkedInvoiceNumber={svc._linked_invoice_number || null}
              onViewInvoice={() => navigate(`/invoices/${svc._linked_invoice_id}`)}
              onUploadDoc={(file) => handleUploadDoc(svc, file)}
              uploading={uploadingId === svc.id}
              onPreviewEmail={() => handlePreviewEmail(svc)}
              onSendTestEmail={() => handleSendTestEmail(svc)}
              sendingEmail={sendingEmailId === svc.id}
              onSendCustomerEmail={() => openSendCustomerModal(svc)}
              isRefunded={svc.refund_status === "full_refund"}
              onRefund={() => openRefundModal(svc)}
              onPreviewRefundEmail={() => handlePreviewRefundEmail(svc)}
              onSendTestRefundEmail={() => handleSendTestRefundEmail(svc)}
              sendingRefundEmail={sendingRefundEmailId === svc.id}
              onSendRefundCustomerEmail={() => openSendRefundCustomerModal(svc)}
            />
          ))}

          {cancelledServices.length > 0 && (
            <details className="text-xs text-slate-400 mt-2">
              <summary className="cursor-pointer">
                {cancelledServices.length} cancelled service{cancelledServices.length > 1 ? "s" : ""} (audit trail)
              </summary>
              <div className="mt-2 space-y-2 pl-2 border-l-2 border-slate-100">
                {cancelledServices.map((svc) => (
                  <div key={svc.id} className="flex items-center justify-between opacity-60">
                    <span>{svc.description}</span>
                    <span className="ml-2">€{Number(svc.amount || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </CardContent>
      </Card>

      {/* ── Add / Edit Dialog ──────────────────────────────────────────── */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Service" : "Add Extra Service"}</DialogTitle>
          </DialogHeader>
          <ServiceForm form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : editId ? "Save Changes" : "Add Service"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Email Preview Dialog ───────────────────────────────────────── */}
      <Dialog open={!!previewEmailId} onOpenChange={(open) => { if (!open) setPreviewEmailId(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Email Preview — {previewData?.subject || "Loading…"}
            </DialogTitle>
          </DialogHeader>

          {previewLoading && <p className="text-sm text-slate-400 py-8 text-center">Loading preview…</p>}

          {previewData && (
            <div className="flex-1 overflow-y-auto space-y-3">
              <div className="text-xs space-y-1 rounded-md bg-slate-50 border p-3">
                <p><span className="text-slate-500 w-12 inline-block">From:</span> {previewData.from}</p>
                <p><span className="text-slate-500 w-12 inline-block">To:</span> {previewData.to || <em className="text-amber-600">No customer email on file — provide one when sending</em>}</p>
                <p><span className="text-slate-500 w-12 inline-block">Subject:</span> {previewData.subject}</p>
              </div>
              {/* sandbox="" prevents scripts and external navigation while inline styles render correctly */}
              <iframe
                srcDoc={previewData.html}
                sandbox=""
                title="Email preview"
                className="border rounded-md w-full"
                style={{ height: "50vh" }}
              />
            </div>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="ghost" onClick={() => setPreviewEmailId(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Refund Dialog ─────────────────────────────────────────────── */}
      <Dialog open={!!refundModal} onOpenChange={(open) => { if (!open) setRefundModal(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-4 h-4" />
              Record Refund
            </DialogTitle>
          </DialogHeader>
          {refundModal && (
            <div className="space-y-4 py-1">
              <p className="text-sm text-slate-700">
                Service: <strong>{refundModal.description}</strong>
                <span className="ml-2 text-slate-500">(€{Number(refundModal.amount || 0).toFixed(2)})</span>
              </p>
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                This records the refund in the accounting system only. No payment is initiated — process the actual payment separately.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Customer Refund Amount *</Label>
                  <Input className="h-8 text-sm mt-1" value={refundForm.customer_refund_amount}
                    onChange={(e) => setRefundForm((p) => ({ ...p, customer_refund_amount: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Customer Refund Date</Label>
                  <Input type="date" className="h-8 text-sm mt-1" value={refundForm.customer_refund_date}
                    onChange={(e) => setRefundForm((p) => ({ ...p, customer_refund_date: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Supplier Refund Amount</Label>
                  <Input className="h-8 text-sm mt-1" value={refundForm.supplier_refund_amount}
                    onChange={(e) => setRefundForm((p) => ({ ...p, supplier_refund_amount: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Supplier Refund Date</Label>
                  <Input type="date" className="h-8 text-sm mt-1" value={refundForm.supplier_refund_date}
                    onChange={(e) => setRefundForm((p) => ({ ...p, supplier_refund_date: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Cancellation Reason</Label>
                <Input className="h-8 text-sm mt-1" value={refundForm.cancellation_reason}
                  onChange={(e) => setRefundForm((p) => ({ ...p, cancellation_reason: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Notes (internal)</Label>
                <Input className="h-8 text-sm mt-1" value={refundForm.refund_notes}
                  onChange={(e) => setRefundForm((p) => ({ ...p, refund_notes: e.target.value }))} />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setRefundModal(null)} disabled={refunding}>Cancel</Button>
            <Button onClick={handleConfirmRefund} disabled={refunding} className="bg-amber-600 hover:bg-amber-700 text-white">
              {refunding ? "Recording…" : "Record Refund"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Refund Email Preview Dialog ────────────────────────────────── */}
      <Dialog open={!!refundEmailPreviewId} onOpenChange={(open) => { if (!open) setRefundEmailPreviewId(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Refund Email Preview — {refundEmailPreviewData?.subject || "Loading…"}
            </DialogTitle>
          </DialogHeader>
          {refundEmailPreviewLoading && <p className="text-sm text-slate-400 py-8 text-center">Loading preview…</p>}
          {refundEmailPreviewData && (
            <div className="flex-1 overflow-y-auto space-y-3">
              <div className="text-xs space-y-1 rounded-md bg-slate-50 border p-3">
                <p><span className="text-slate-500 w-12 inline-block">From:</span> {refundEmailPreviewData.from}</p>
                <p><span className="text-slate-500 w-12 inline-block">To:</span> {refundEmailPreviewData.to || <em className="text-amber-600">No customer email on file — provide one when sending</em>}</p>
                <p><span className="text-slate-500 w-12 inline-block">Subject:</span> {refundEmailPreviewData.subject}</p>
              </div>
              <iframe
                srcDoc={refundEmailPreviewData.html}
                sandbox=""
                title="Refund email preview"
                className="border rounded-md w-full"
                style={{ height: "50vh" }}
              />
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="ghost" onClick={() => setRefundEmailPreviewId(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Send Refund Email to Customer Dialog ──────────────────────── */}
      <Dialog open={!!refundEmailCustomerModal} onOpenChange={(open) => { if (!open) setRefundEmailCustomerModal(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-4 h-4" />
              Send Refund Notification to Customer
            </DialogTitle>
          </DialogHeader>
          {refundEmailCustomerModal && (
            <div className="space-y-4 py-1">
              <p className="text-sm text-slate-700">
                Sending refund notification for: <strong>{refundEmailCustomerModal.description}</strong>
              </p>
              <div className="rounded-md bg-slate-50 border p-3 text-sm">
                <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Sending to:</span>
                {refundEmailResolvedRecipient === null ? (
                  <p className="mt-1 text-slate-400 text-xs">Resolving recipient…</p>
                ) : refundEmailResolvedRecipient ? (
                  <p className="mt-1 font-medium text-slate-800">{refundEmailResolvedRecipient}</p>
                ) : (
                  <p className="mt-1 text-amber-700 text-xs">No email found on linked booking — enter one below.</p>
                )}
              </div>
              <div>
                <Label className="text-xs text-slate-600">Override recipient (optional)</Label>
                <Input
                  className="h-8 text-sm mt-1"
                  placeholder="Leave blank to use the address above"
                  value={refundEmailRecipientOverride}
                  onChange={(e) => setRefundEmailRecipientOverride(e.target.value)}
                />
              </div>
              <p className="text-xs text-slate-500">
                This notifies the customer that their refund has been processed. A copy is sent to the agency inbox. Internal notes are not included.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setRefundEmailCustomerModal(null)} disabled={sendingRefundEmailCustomer}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSendRefundCustomer}
              disabled={sendingRefundEmailCustomer || refundEmailResolvedRecipient === null || (refundEmailResolvedRecipient === "" && !refundEmailRecipientOverride.trim())}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {sendingRefundEmailCustomer ? "Sending…" : "Send Refund Notification"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Send to Customer Confirmation Dialog ──────────────────────── */}
      <Dialog open={!!sendCustomerModal} onOpenChange={(open) => { if (!open) setSendCustomerModal(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-4 h-4" />
              Send Confirmation to Customer
            </DialogTitle>
          </DialogHeader>

          {sendCustomerModal && (
            <div className="space-y-4 py-1">
              <p className="text-sm text-slate-700">
                Sending confirmation for: <strong>{sendCustomerModal.description}</strong>
              </p>

              {sendCustomerModal.service_status !== "confirmed" && sendCustomerModal.service_status !== "delivered" && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                  Fulfilment status is <strong>{sendCustomerModal.service_status}</strong>. The email will not claim the service is confirmed. Change the status to <em>Confirmed</em> first if appropriate.
                </div>
              )}

              <div className="rounded-md bg-slate-50 border p-3 text-sm">
                <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Sending to:</span>
                {resolvedRecipient === null ? (
                  <p className="mt-1 text-slate-400 text-xs">Resolving recipient…</p>
                ) : resolvedRecipient ? (
                  <p className="mt-1 font-medium text-slate-800">{resolvedRecipient}</p>
                ) : (
                  <p className="mt-1 text-amber-700 text-xs">No email found on linked booking — enter one below.</p>
                )}
              </div>

              <div>
                <Label className="text-xs text-slate-600">Override recipient (optional)</Label>
                <Input
                  className="h-8 text-sm mt-1"
                  placeholder="Leave blank to use the address above"
                  value={recipientOverride}
                  onChange={(e) => setRecipientOverride(e.target.value)}
                />
              </div>

              <p className="text-xs text-slate-500">
                A copy will be sent to the agency inbox. Internal notes and supplier cost are not included in the email.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setSendCustomerModal(null)} disabled={sendingCustomer}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSendCustomer}
              disabled={sendingCustomer || resolvedRecipient === null || (resolvedRecipient === "" && !recipientOverride.trim())}
            >
              {sendingCustomer ? "Sending…" : "Send to Customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ServiceRow({
  svc,
  onEdit,
  onDelete,
  onGenerateInvoice,
  generatingInv,
  linkedInvoiceId,
  linkedInvoiceNumber,
  onViewInvoice,
  onUploadDoc,
  uploading,
  onPreviewEmail,
  onSendTestEmail,
  sendingEmail,
  onSendCustomerEmail,
  isRefunded,
  onRefund,
  onPreviewRefundEmail,
  onSendTestRefundEmail,
  sendingRefundEmail,
  onSendRefundCustomerEmail,
}) {
  const fileInputRef = useRef(null);

  const emailSent = !!svc.confirmation_email_sent_at;

  return (
    <div className="border rounded-lg p-3 space-y-2 bg-slate-50/50">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {svc.service_type && (
              <Badge variant="outline" className="text-xs capitalize shrink-0">
                {SERVICE_TYPE_LABELS[svc.service_type] || svc.service_type}
              </Badge>
            )}
            <Badge
              variant={SERVICE_STATUS_VARIANTS[svc.service_status] || "secondary"}
              className="text-xs shrink-0 capitalize"
            >
              {svc.service_status || "pending"}
            </Badge>
            {linkedInvoiceNumber && (
              <Badge variant="outline" className="text-xs shrink-0 text-blue-700 border-blue-300 bg-blue-50">
                <FileText className="w-2.5 h-2.5 mr-1" />
                {linkedInvoiceNumber}
              </Badge>
            )}
            {isRefunded && (
              <Badge variant="outline" className="text-xs shrink-0 text-orange-700 border-orange-300 bg-orange-50">
                <RotateCcw className="w-2.5 h-2.5 mr-1" />
                REFUNDED
              </Badge>
            )}
          </div>
          <p className="text-sm font-medium mt-1 truncate">{svc.description}</p>
          {svc.supplier_name && (
            <p className="text-xs text-slate-500">{svc.supplier_name}</p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-semibold">€{Number(svc.amount || 0).toFixed(2)}</p>
          {svc.supplier_cost > 0 && (
            <p className="text-xs text-slate-500">Cost: €{Number(svc.supplier_cost).toFixed(2)}</p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-1 text-xs text-slate-500">
        {svc.passenger_name && <span>Pax: {svc.passenger_name}</span>}
        {svc.segment_info && <span>· {svc.segment_info}</span>}
        {svc.service_date && <span>· {svc.service_date}</span>}
        {svc.confirmation_reference && <span>· Ref: {svc.confirmation_reference}</span>}
        {svc.payment_status && (
          <span className={svc.payment_status === "Paid" ? "text-emerald-600" : "text-amber-600"}>
            · {svc.payment_status}
          </span>
        )}
      </div>

      {svc.supplier_document_url && (
        <a
          href={svc.supplier_document_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-blue-600 hover:underline"
        >
          Supplier doc
        </a>
      )}

      {/* ── Service management actions ─────────────────────────────────── */}
      <div className="flex flex-wrap gap-1.5 pt-1">
        <Button size="xs" variant="ghost" className="h-6 text-xs px-2" onClick={onEdit} disabled={isRefunded}>
          <Pencil className="w-3 h-3 mr-1" /> Edit
        </Button>

        {linkedInvoiceId ? (
          <Button
            size="xs"
            variant="ghost"
            className="h-6 text-xs px-2 text-blue-700 hover:text-blue-900"
            onClick={onViewInvoice}
            title={`View invoice ${linkedInvoiceNumber}`}
          >
            <FileText className="w-3 h-3 mr-1" /> View Invoice
          </Button>
        ) : (
          <Button
            size="xs"
            variant="ghost"
            className="h-6 text-xs px-2"
            onClick={onGenerateInvoice}
            disabled={generatingInv || isRefunded}
            title={isRefunded ? "Cannot generate invoice for a refunded service" : "Generate invoice from this service"}
          >
            <FileText className="w-3 h-3 mr-1" />
            {generatingInv ? <Loader2 className="w-3 h-3 animate-spin" /> : "Generate Invoice"}
          </Button>
        )}

        <label
          className={`inline-flex items-center h-6 text-xs px-2 rounded cursor-pointer hover:bg-slate-100 ${
            uploading ? "opacity-50 pointer-events-none" : ""
          }`}
          title="Attach supplier document"
        >
          <Upload className="w-3 h-3 mr-1" />
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Doc"}
          <input
            ref={(r) => { fileInputRef.current = r; }}
            type="file"
            className="hidden"
            accept="application/pdf,image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUploadDoc(file);
              e.target.value = "";
            }}
          />
        </label>

        {!isRefunded && (
          <Button
            size="xs"
            variant="ghost"
            className="h-6 text-xs px-2 text-orange-600 hover:text-orange-800"
            onClick={onRefund}
            title="Record a refund for this service"
          >
            <RotateCcw className="w-3 h-3 mr-1" /> Refund
          </Button>
        )}

        <Button
          size="xs"
          variant="ghost"
          className="h-6 text-xs px-2 text-red-500 hover:text-red-700"
          onClick={onDelete}
          disabled={isRefunded}
          title={isRefunded ? "Cannot delete a refunded service entry" : undefined}
        >
          <Trash2 className="w-3 h-3 mr-1" /> Delete
        </Button>
      </div>

      {/* ── Email communication row ────────────────────────────────────── */}
      <div className="rounded-md bg-slate-100/70 border border-slate-200 px-2.5 py-2 space-y-1.5">
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
          Customer email
        </p>

        {/* Send status summary */}
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
          {svc.confirmation_test_sent_at && (
            <span className="flex items-center gap-1 text-slate-500">
              <Mail className="w-3 h-3" />
              Test sent {new Date(svc.confirmation_test_sent_at).toLocaleDateString()}
            </span>
          )}
          {emailSent && (
            <span className="flex items-center gap-1 text-emerald-600 font-medium">
              <CheckCircle2 className="w-3 h-3" />
              Sent to {svc.confirmation_email_sent_to
                ? <strong className="ml-0.5">{svc.confirmation_email_sent_to}</strong>
                : "customer"} on {new Date(svc.confirmation_email_sent_at).toLocaleDateString()}
            </span>
          )}
        </div>

        {/* Email action buttons */}
        <div className="flex flex-wrap gap-1.5">
          <Button
            size="xs"
            variant="outline"
            className="h-6 text-xs px-2 bg-white"
            onClick={onPreviewEmail}
            title="Preview the confirmation email without sending"
          >
            <Eye className="w-3 h-3 mr-1" /> Preview
          </Button>

          <Button
            size="xs"
            variant="outline"
            className="h-6 text-xs px-2 bg-white border-amber-300 text-amber-700 hover:bg-amber-50"
            onClick={onSendTestEmail}
            disabled={sendingEmail || isRefunded}
            title="Send a test email to the agency inbox — not sent to customer"
          >
            {sendingEmail
              ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              : <Mail className="w-3 h-3 mr-1" />}
            Test
            <span className="ml-1 bg-amber-200 text-amber-800 text-[9px] font-bold px-1 py-0.5 rounded uppercase tracking-wide">TEST</span>
          </Button>

          <Button
            size="xs"
            variant="outline"
            className={`h-6 text-xs px-2 bg-white ${
              emailSent
                ? "border-slate-300 text-slate-600 hover:bg-slate-50"
                : "border-emerald-400 text-emerald-700 hover:bg-emerald-50"
            }`}
            onClick={onSendCustomerEmail}
            disabled={isRefunded}
            title={isRefunded ? "Cannot send email for a refunded service" : emailSent
              ? `Resend — last sent ${new Date(svc.confirmation_email_sent_at).toLocaleDateString()} to ${svc.confirmation_email_sent_to || "customer"}`
              : "Send confirmation email to the customer"}
          >
            <Send className="w-3 h-3 mr-1" />
            {emailSent ? "Resend" : "Send to Customer"}
          </Button>
        </div>
      </div>

      {isRefunded && (
        <div className="rounded-md bg-orange-50/70 border border-orange-200 px-2.5 py-2 space-y-1.5 mt-1">
          <p className="text-[10px] font-semibold text-orange-400 uppercase tracking-wide">
            Refund email
          </p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
            {svc.refund_test_email_sent_at && (
              <span className="flex items-center gap-1 text-slate-500">
                <Mail className="w-3 h-3" />
                Test sent {new Date(svc.refund_test_email_sent_at).toLocaleDateString()}
              </span>
            )}
            {svc.refund_email_sent_at && (
              <span className="flex items-center gap-1 text-emerald-600 font-medium">
                <CheckCircle2 className="w-3 h-3" />
                Sent to {svc.refund_email_sent_to
                  ? <strong className="ml-0.5">{svc.refund_email_sent_to}</strong>
                  : "customer"} on {new Date(svc.refund_email_sent_at).toLocaleDateString()}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Button
              size="xs"
              variant="outline"
              className="h-6 text-xs px-2 bg-white"
              onClick={onPreviewRefundEmail}
              title="Preview the refund notification email"
            >
              <Eye className="w-3 h-3 mr-1" /> Preview
            </Button>
            <Button
              size="xs"
              variant="outline"
              className="h-6 text-xs px-2 bg-white border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={onSendTestRefundEmail}
              disabled={sendingRefundEmail}
              title="Send a test refund email to the agency inbox"
            >
              {sendingRefundEmail
                ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                : <Mail className="w-3 h-3 mr-1" />}
              Test
              <span className="ml-1 bg-amber-200 text-amber-800 text-[9px] font-bold px-1 py-0.5 rounded uppercase tracking-wide">TEST</span>
            </Button>
            <Button
              size="xs"
              variant="outline"
              className={`h-6 text-xs px-2 bg-white ${
                svc.refund_email_sent_at
                  ? "border-slate-300 text-slate-600 hover:bg-slate-50"
                  : "border-orange-400 text-orange-700 hover:bg-orange-50"
              }`}
              onClick={onSendRefundCustomerEmail}
              title={svc.refund_email_sent_at
                ? `Resend — last sent ${new Date(svc.refund_email_sent_at).toLocaleDateString()} to ${svc.refund_email_sent_to || "customer"}`
                : "Send refund notification to the customer"}
            >
              <Send className="w-3 h-3 mr-1" />
              {svc.refund_email_sent_at ? "Resend" : "Send to Customer"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ServiceForm({ form, setForm }) {
  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  return (
    <div className="space-y-4">
      {/* Service type + Status */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Service Type</Label>
          <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
            <SelectTrigger className="h-8 text-sm mt-1">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SERVICE_TYPE_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Fulfilment Status</Label>
          <Select value={form.service_status} onValueChange={(v) => set("service_status", v)}>
            <SelectTrigger className="h-8 text-sm mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Description */}
      <div>
        <Label className="text-xs">Description *</Label>
        <Input
          className="h-8 text-sm mt-1"
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
          placeholder="e.g. Extra 23kg bag — EK415"
        />
      </div>

      {/* Supplier + Passenger */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Provider / Supplier</Label>
          <Input
            className="h-8 text-sm mt-1"
            value={form.supplier_name}
            onChange={(e) => set("supplier_name", e.target.value)}
            placeholder="Emirates, Dragonpass…"
          />
        </div>
        <div>
          <Label className="text-xs">Passenger</Label>
          <Input
            className="h-8 text-sm mt-1"
            value={form.passenger_name}
            onChange={(e) => set("passenger_name", e.target.value)}
            placeholder="As on booking"
          />
        </div>
      </div>

      {/* Segment + Service date */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Flight / Segment</Label>
          <Input
            className="h-8 text-sm mt-1"
            value={form.segment_info}
            onChange={(e) => set("segment_info", e.target.value)}
            placeholder="AMS→DXB / EK415"
          />
        </div>
        <div>
          <Label className="text-xs">Service Date</Label>
          <Input
            type="date"
            className="h-8 text-sm mt-1"
            value={form.service_date}
            onChange={(e) => set("service_date", e.target.value)}
          />
        </div>
      </div>

      {/* Confirmation reference */}
      <div>
        <Label className="text-xs">Service Confirmation Reference</Label>
        <Input
          className="h-8 text-sm mt-1"
          value={form.confirmation_reference}
          onChange={(e) => set("confirmation_reference", e.target.value)}
          placeholder="Provider confirmation code"
        />
      </div>

      {/* Selling price + Supplier cost */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Selling Price (€) *</Label>
          {/* text input to accept both period and European comma decimals */}
          <Input
            type="text"
            inputMode="decimal"
            className="h-8 text-sm mt-1"
            value={form.amount}
            onChange={(e) => set("amount", e.target.value)}
            placeholder="e.g. 87.00 or 87,00"
          />
        </div>
        <div>
          <Label className="text-xs">Supplier Cost (€)</Label>
          <Input
            type="text"
            inputMode="decimal"
            className="h-8 text-sm mt-1"
            value={form.supplier_cost}
            onChange={(e) => set("supplier_cost", e.target.value)}
            placeholder="e.g. 71.41 or 71,41"
          />
        </div>
      </div>

      {/* VAT type */}
      <div>
        <Label className="text-xs">VAT Classification</Label>
        <Select value={form.vat_type} onValueChange={(v) => set("vat_type", v)}>
          <SelectTrigger className="h-8 text-sm mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VAT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Payment */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Payment Mode</Label>
          <Select value={form.payment_mode} onValueChange={(v) => set("payment_mode", v)}>
            <SelectTrigger className="h-8 text-sm mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
              <SelectItem value="Credit Card">Credit Card</SelectItem>
              <SelectItem value="Cash">Cash</SelectItem>
              <SelectItem value="Tikkie">Tikkie</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Payment Status</Label>
          <Select value={form.payment_status} onValueChange={(v) => set("payment_status", v)}>
            <SelectTrigger className="h-8 text-sm mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Paid">Paid</SelectItem>
              <SelectItem value="Unpaid">Unpaid</SelectItem>
              <SelectItem value="Partial">Partial</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Amount received + date received */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Amount Received (€)</Label>
          <Input
            type="text"
            inputMode="decimal"
            className="h-8 text-sm mt-1"
            value={form.amount_received}
            onChange={(e) => set("amount_received", e.target.value)}
            placeholder="e.g. 87.00 or 87,00"
          />
        </div>
        <div>
          <Label className="text-xs">Date Received</Label>
          <Input
            type="date"
            className="h-8 text-sm mt-1"
            value={form.date_received}
            onChange={(e) => set("date_received", e.target.value)}
          />
        </div>
      </div>

      {/* Notes */}
      <div>
        <Label className="text-xs">Notes <span className="text-slate-400">(internal — not sent to customer)</span></Label>
        <Input
          className="h-8 text-sm mt-1"
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          placeholder="Internal notes"
        />
      </div>
    </div>
  );
}
